import React, { useEffect, useState } from 'react'
import { UploadIcon } from '../../../../svg/UploadIcon'
import { Xicon } from '../../../../svg/Xicon'
import Alert from '../../../../_metronic/partials/alert'
import {
  initiateFsTransaction,
  addFinancialYear,
} from '../../../../components/BankStatementValidator/utils/fsTransaction'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileUploadInstructionComponent, { InstructionsData } from '../../../../components/FileUploadInstruction/FileUploadInstruction'
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from "@mui/icons-material/Lock";
import PdfDark from '../../../../svg/PdfDark'
import { Spinner } from 'react-bootstrap-v5'

interface FinancialStatementsUploadProps {
  fileGroup: any[]
  applicationid: string
  handleFileUpload: (event: any, type: string, fileTypes: string[], year?: number) => void
  removeSingleFile: (id: string) => void
  queueFsFile: (year: number, file: File, prefixedName: string) => void
  showNewInstructions?: boolean
  setIsFinancialStatementUploading?: any
  uploadingType?: string | null
  uploadingFinancialStatement?: {type: string, isLoading: boolean}[]
  setUploadingFinancialStatement?: any
}

/**
 * FinancialStatementsUpload Component
 *
 * Handles multi-year financial statement uploads for loan applications.
 * Features:
 * - Three year boxes: 2023, 2024, and 2025/26 (management year)
 * - Files are prefixed with year keys (y1-, y2-, y3-) to maintain organization
 * - Drag & drop functionality for each year box
 * - Files remain in their designated year box permanently (no shifting as years progress)
 * - Only one file per year is allowed
 */
const FinancialStatementsUploadForAgentClientOnboarding: React.FC<FinancialStatementsUploadProps> = ({
  fileGroup,
  applicationid,
  handleFileUpload,
  removeSingleFile,
  queueFsFile,
  showNewInstructions = false,
  setIsFinancialStatementUploading,
  uploadingType,
  uploadingFinancialStatement,
  setUploadingFinancialStatement
}) => {
  // Initiate FS flow on mount with static payload
  useEffect(() => {
    const initiateFs = async () => {
      try {
        await initiateFsTransaction()
      } catch (error) {
        console.error('Failed to initiate FS transaction:', error)
      }
    }

    initiateFs()
  }, [])
  /**
   * Dynamic year definitions based on current year for financial years.
   * Financial year logic: if current year is 2025, then:
   * - Current management year is 2024-25 (FY ending in 2025)
   * - Past years are 2023 and 2022
   */
  const currentYear = new Date().getFullYear()
  const years = [
    { year: currentYear - 3, label: `${currentYear - 3}`, key: 'y1' }, // 3 years ago (2022)
    { year: currentYear - 2, label: `${currentYear - 2}`, key: 'y2' }, // 2 years ago (2023)
  ]
  const managementYear = {
    year: currentYear - 1,
    label: `${currentYear - 1}-${currentYear.toString().slice(-2)}`,
    key: 'y3',
  } // Current financial year (2024-25)

  // Filter all uploaded files to get only financial statements
  const financialStatements = fileGroup.filter(({ type }: any) => type === 'financial-statement')

  /**
   * Retrieves files for a specific year based on filename prefix.
   * Files are organized using year keys for financial years:
   * - y1- prefix = currentYear-3 files (2022)
   * - y2- prefix = currentYear-2 files (2023)
   * - y3- prefix = currentYear-1 files (2024 for FY 2024-25)
   */
  const getFilesForYear = (year: number) => {
    const yearKey = year === currentYear - 3 ? 'y1' : year === currentYear - 2 ? 'y2' : 'y3'

    return financialStatements.filter((file: any) => {
      const filename = file.file?.filename || ''
      return filename.startsWith(`${yearKey}-`)
    })
  }

  /**
   * Check if a specific year already has a file
   */
  const hasFileForYear = (year: number) => {
    return getFilesForYear(year).length > 0
  }

  /**
   * State to show upload error message
   */
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [alertType, setAlertType] = useState<any>('primary')
  const [alertMsg, setAlertMsg] = useState<string>('')

  /**
   * Custom FS file upload handler that calls addFinancialYear API
   * @param file - The uploaded file
   * @param year - Year for the upload box (2022, 2023, 2024)
   */
  const handleFsFileUpload = async (file: File, year: number) => {
    const filesForThisYear = getFilesForYear(year)
    if (filesForThisYear.length > 0) {
      setShowAlert(true)
      setAlertType('primary')
      setAlertMsg(`Only one file per year is allowed. Please remove the existing file for ${year} first.`)
      setTimeout(() => setShowAlert(false), 5000)
      return
    }
  
    const fsToken = localStorage.getItem('fsTransactionId')
    if (!fsToken) {
      setShowAlert(true)
      setAlertType('primary')
      setAlertMsg('Please wait for the system to initialize before uploading files.')
      setTimeout(() => setShowAlert(false), 5000)
      return
    }
  
    const currentCalendarYear = new Date().getFullYear()
    const currentFinancialYear = currentCalendarYear - 1
    const isCurrentYear = year === currentFinancialYear
  
    const event = { target: { files: [file], validity: { valid: true } } }
    setIsFinancialStatementUploading(true)

    setUploadingFinancialStatement((prev: any) => {
      const existingItem = prev.find((item: any) => item.type === `upload-file-${year}`)

      if (existingItem) {
        // Update existing item's isLoading to true
        return prev.map((item: any) =>
          item.type === `upload-file-${year}`
            ? { ...item, isLoading: true }
            : item
        )
      } else {
        // Add new item
        return [...prev, { type: `upload-file-${year}`, isLoading: true }]
      }
    })

    try {
      if (isCurrentYear) {
        // y3: show immediately (GraphQL upload), but remember to call addFinancialYear on submit
        queueFsFile(year, file, `${year === currentCalendarYear - 3 ? 'y1' : year === currentCalendarYear - 2 ? 'y2' : 'y3'}-${file.name}`)
        handleFileUpload(event, 'financial-statement', ['application/pdf'], year)
      } else {
        // y1/y2: call addFinancialYear now AND upload via GraphQL so it appears
        await addFinancialYear(file, year.toString(), applicationid, false)
        handleFileUpload(event, 'financial-statement', ['application/pdf'], year)
      }
    } catch (err) {
      console.error('FS upload failed, falling back to GraphQL only:', err)
      // Fallback: still show by uploading via GraphQL
      handleFileUpload(event, 'financial-statement', ['application/pdf'], year)
    } finally {
      setIsFinancialStatementUploading(false)
    }
  }
  
  
  
  
  /**
   * Handles file drop events for drag and drop uploads.
   * Passes the year parameter to ensure files get the correct prefix.
   */
  const handleDrop = (e: React.DragEvent, year?: number) => {
    e.preventDefault()
    e.stopPropagation()

    // If year is specified and already has a file, don't allow upload
    if (year && hasFileForYear(year)) {
      setShowAlert(true)
      setAlertType('primary')
      setAlertMsg('Only one file per year allowed.')
      setTimeout(() => setShowAlert(false), 5000) // Clear error after 5 seconds
      return
    }

    // Check if user is trying to upload multiple files
    const files = e.dataTransfer.files
    if (files.length > 1) {
      setShowAlert(true)
      setAlertType('primary')
      setAlertMsg('Only one file per year allowed.')
      setTimeout(() => setShowAlert(false), 5000) // Clear error after 5 seconds
      return
    }

    // Process single file upload
    if (files.length === 1 && year) {
      // Call our custom FS upload handler for drag & drop
      handleFsFileUpload(files[0], year)
    } else if (files.length === 1) {
      // Fallback to normal upload
      const event = { target: { files, validity: { valid: true } } }
      handleFileUpload(event, 'financial-statement', ['application/pdf'])
    }
  }

  /**
   * Prevents default browser behavior during drag over events.
   * Required for drag & drop functionality to work properly.
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  /**
   * Handles drag leave events to clean up any visual feedback.
   */
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  /**
   * Handles file input change events when users select files via the file picker.
   * Passes the year parameter to ensure files get the correct prefix.
   */
  const handleFileChange = (e: any, year?: number) => {
    const files = e.target.files
    if (files && files.length > 0 && year) {
      // Call our custom FS upload handler
      handleFsFileUpload(files[0], year)
    } else {
      // Fallback to normal upload
      handleFileUpload(e, 'financial-statement', ['application/pdf'], year)
    }
  }

  const ActiveYearUploadBox = ({ year, label }: { year: number; label: string }) => {
    const inputId = `upload-file-${year}`
    const filesForThisYear = getFilesForYear(year)
    const uploadsAllowed = filesForThisYear.length === 0

    // Common box styles to ensure consistent sizing between states
    const boxStyles = {
      border: '2px dashed #D1D5DB',
      borderRadius: '8px',
      padding: '30px 20px',
      textAlign: 'center' as const,
      backgroundColor: '#F9FAFB',
      height: '225px',
      minHeight: '150px',
      width: '100%',
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'center',
      alignItems: 'center',
      position: 'relative' as const,
    }

    return (
      <div style={{ flex: 1, minWidth: '300px' }}>
        {uploadsAllowed ?  (
          <div
            style={boxStyles}
            onDrop={(e) => handleDrop(e, year)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            {
              uploadingFinancialStatement?.find(item => item.type === inputId && item?.isLoading) ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <div style={{ marginBottom: "6px", fontSize: "18px" }}>Uploading file...</div>
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <input
                    id={inputId}
                    type='file'
                    accept='.pdf'
                    onChange={(e) => handleFileChange(e, year)}
                    style={{ display: 'none' }}
                    disabled={uploadingFinancialStatement?.some(item => item.isLoading)}
                  />
                  <label htmlFor={inputId} style={{ cursor: 'pointer', width: '100%' }}>
                    <div style={{ marginBottom: '12px' }}>
                      <UploadIcon color='#9CA3AF' width='40' height='40' />
                    </div>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '8px',
                      }}
                    >
                      Drag & Drop or Click to Upload {label}
                    </div>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '8px',
                      }}
                    >
                      Financial Statements
                    </div>
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>Supported formats: PDF</div>
                  </label>
                </>
              )}
          </div>
        ) : (
            <div
              style={boxStyles}
              onDrop={(e) => handleDrop(e, year)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {
                uploadingFinancialStatement?.find(item => item.type === inputId && item?.isLoading) ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <div style={{ marginBottom: "6px", fontSize: "18px" }}>Uploading file...</div>
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <>
                    <input
                      id={inputId}
                      type='file'
                      accept='.pdf'
                      onChange={(e) => handleFileChange(e, year)}
                      style={{ display: 'none' }}
                      disabled={uploadingFinancialStatement?.some(item => item?.isLoading)}
                    />
                    <label htmlFor={inputId} style={{ cursor: 'pointer', width: '100%' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <UploadIcon color='#9CA3AF' width='40' height='40' />
                      </div>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px',
                        }}
                      >
                        Drag & Drop or Click to Upload {label}
                      </div>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '8px',
                        }}
                      >
                        Financial Statements
                      </div>
                      <div style={{ fontSize: '14px', color: '#6B7280' }}>Supported formats: PDF</div>
                    </label>
                  </>
                )}
            </div>
          )}

        {filesForThisYear.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              {filesForThisYear.map((file: any) => (
                <div
                  key={file.id}
                  style={{
                    fontSize: 14,
                    color: '#616B75',
                    background: '#F9FAFA',
                    padding: '8px 12px',
                    display: 'flex',
                    borderRadius: '8px',
                    border: '1px solid #E0E0E0',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: 'fit-content',
                  }}
                  title={file.file.filename}
                >
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {(() => {
                      // Remove the year prefix from display
                      const filename = file.file.filename
                      const withoutPrefix = filename.replace(/^y[123]-/, '')
                      const parts = withoutPrefix.split('.')
                      const name = parts[0].substring(0, 20)
                      const extension = parts[1]
                      return `${name}...${extension}`
                    })()}
                  </span>
                  <Xicon
                    onClick={() => removeSingleFile(file.id)}
                    className='cursor-pointer'
                    style={{ marginLeft: '8px', flexShrink: 0 }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const FinancialStatementsUploadInstructions: InstructionsData = {
    heading: "Tips In Uploading: Management Accounts",
    data: [
    {
      icon: <FileUploadOutlinedIcon sx={{ fontSize: 20 }} />, 
      title: "What to Upload ?",
      description: (
        <>
          Please upload your Year-to-Date Management Account (e.g., Jan 2025 to
          date) or Full-Year Management Account
          <br /><br />
          <b>Example:</b> (FY2024 Unaudited financial statements), whichever is available
        </>
      ),
    },
    {
      icon: <PdfDark sx={{ fontSize: 20 }} />,
      title: "Only PDF(s) allowed",
      description: (
        <>
          Ensure the file type belongs to one of the supported formats
          before submitting
        </>
      ),
    },
    {
      icon: <LockIcon sx={{ fontSize: 20 }} />,
      title: "Password Protected ?",
      description: (
        <>
          Ensure any password protection is removed to prevent submission errors
        </>
      ),
    },
    {
      icon: <LibraryAddIcon sx={{ fontSize: 20, transform: 'rotate(180deg) scaleX(-1)' }} />,
      title: "Combine in One file",
      description: (
        <>
          Combine the Profit &amp; Loss, Balance Sheet, and Cash Flow Statement
          into a single file and upload it in the “Drag and Drop” field below
        </>
      ),
    },
    {
      icon: <EmailIcon sx={{ fontSize: 20 }} />,
      title: "Contact us",
      description: (
        <>
          In case if you are facing any issue while uploading financial statements,
          please contact us on <b> credit.ops@ccrpartners.co</b>
        </>
      ),
    },
  ]
};

  return (
    <div style={{ backgroundColor: '#FFFFFF', padding: '24px', borderRadius: '8px' }}>
      {/* Error message display */}

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2
          style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#000',
            marginBottom: '0',
          }}
        >
          Last 3 Years Financial Statements
        </h2>
      </div>
 {/* Tips Section */}
      {!showNewInstructions ?
      (<div className='bank-statement-upload mb-3 rounded p-4'>
        <h3
          className='d-flex align-items-center'
          style={{
            fontWeight: 500,
            fontSize: '14px',
            marginBottom: '8px',
          }}
        >
          Tips for Uploading Financial Statements:
        </h3>
        <ul className='fs-4'>
          <li>
            <span>
              Please upload your Year-to-Date Management Account (e.g., Jan 2025 to date) or
              Full-Year Management Account (e.g., FY2024 Unaudited financial statements), whichever
              is available.
            </span>
          </li>
          <li>
            <span>
              Combine the Profit & Loss, Balance Sheet, and Cash Flow Statement into a single file
              and upload it in the "Drag and Drop" field below.
            </span>
          </li>
          <li>
            <span>
              Ensure the file type belongs to one of the supported formats before submitting.
            </span>
          </li>
          <li>
            <span>Ensure any password protection is removed to prevent submission errors.</span>
          </li>
          <li>
            <span>
              If you experience any issues uploading your financial statements, please contact us at{' '}
              <a
                href='mailto:loansupport@ccrpartners.co'
                style={{ fontWeight: 'bold' }}
                className='text-primary-dark text-decoration-underline'
              >
                loansupport@ccrpartners.co
              </a>
              .
            </span>
          </li>
        </ul>
      </div>) :
      (
        <FileUploadInstructionComponent cardData={FinancialStatementsUploadInstructions}/>
      )
      }

      {/* Last 2 Years Upload Sections */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          marginBottom: '32px',
          flexWrap: 'wrap',
        }}
      >
        {years.map((yearData) => (
          <ActiveYearUploadBox
            key={yearData.year}
            year={yearData.year}
            label={yearData.label + ' Audited/Unaudited'}
          />
        ))}
      </div>

     
      {/* Management Year Upload Section - Active Upload */}
      <div style={{ marginBottom: '20px' }}>
        <ActiveYearUploadBox
          key={managementYear.year}
          year={managementYear.year}
          label={managementYear.label + ' Audited/Unaudited'}
        />
      </div>

      {showAlert && (
        <Alert variant={alertType} showAlert={showAlert} setShowAlert={setShowAlert}>
          {alertMsg}
        </Alert>
      )}
    </div>
  )
}

export default FinancialStatementsUploadForAgentClientOnboarding
